import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useToast } from "@/components/toast";
import { AdminPage, Card, Gate, Meta } from "@/components/admin-ui";
import { Button, EmptyState, ErrorState, Loading, Pill } from "@/components/ui";

const tone = (s) => (s === "completed" ? "success" : s === "rejected" ? "error" : "gold");

export default function AdminDeletionRequests() {
  return <Gate perm="members" testId="admin-deletion-forbidden"><Inner /></Gate>;
}

function Inner() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-deletion-requests"], queryFn: () => api("/admin/deletion-requests") });
  const mark = useMutation({
    mutationFn: ({ id, status }) => api(`/admin/deletion-requests/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-deletion-requests"] }); toast("Request updated", "success"); },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <AdminPage title="Deletion requests" testId="admin-deletion-screen">
      <p className="text-sm text-muted-foreground">Requests submitted on the public deletion page (/delete-account). Find the member in Members, delete or deactivate the account, then mark it completed.</p>
      {isLoading ? <Loading /> : isError ? <ErrorState testId="admin-deletion-error" message={error.message} onRetry={() => refetch()} /> : data.requests.length ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {data.requests.map((r) => (
            <Card key={r.id} testId={`admin-deletion-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 flex-1 break-all text-[15px] font-bold" data-testid={`admin-deletion-email-${r.id}`}>{r.email}</p>
                <Pill label={r.status} tone={tone(r.status)} testId={`admin-deletion-status-${r.id}`} />
              </div>
              <Meta>{fmtDate(r.created_at)} · {r.user_id ? "Matches an account" : "No matching account"}</Meta>
              {r.reason ? <p className="mt-1 text-sm">{r.reason}</p> : null}
              {r.status === "open" ? (
                <div className="mt-3 flex gap-2">
                  <Button testId={`admin-deletion-complete-${r.id}`} title="Completed" className="flex-1" loading={mark.isPending && mark.variables?.id === r.id && mark.variables?.status === "completed"}
                    onClick={() => mark.mutate({ id: r.id, status: "completed" })} />
                  <Button testId={`admin-deletion-reject-${r.id}`} title="Reject" variant="outline" className="flex-1" loading={mark.isPending && mark.variables?.id === r.id && mark.variables?.status === "rejected"}
                    onClick={() => mark.mutate({ id: r.id, status: "rejected" })} />
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      ) : <EmptyState testId="admin-deletion-empty" icon={Trash2} title="No deletion requests" />}
    </AdminPage>
  );
}
